import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useLang } from "../../contexts/LangContext";
import { t } from "../../i18n/translations";
import { Calendar, ClipboardList, Users, TrendingUp, Gift, Mail } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminDashboard() {
  const { lang } = useLang();
  const [stats, setStats] = useState({ pending: 0, confirmed: 0, customers: 0, services: 0, vouchers_pending: 0, msgs_unread: 0 });
  const [recent, setRecent] = useState([]);
  const [recentMsgs, setRecentMsgs] = useState([]);

  useEffect(() => {
    async function load() {
      try {
        const [b, c, s, v, m] = await Promise.all([
          api.get("/admin/bookings"),
          api.get("/admin/customers"),
          api.get("/admin/services"),
          api.get("/admin/vouchers"),
          api.get("/admin/messages"),
        ]);
        const bookings = b.data || [];
        const vouchers = v.data || [];
        const messages = m.data || [];
        setRecent(bookings.slice(0, 5));
        setRecentMsgs(messages.filter((x) => !x.read).slice(0, 3));
        setStats({
          pending: bookings.filter((x) => x.status === "pending").length,
          confirmed: bookings.filter((x) => x.status === "confirmed").length,
          customers: (c.data || []).length,
          services: (s.data || []).length,
          vouchers_pending: vouchers.filter((x) => x.status !== "sent").length,
          msgs_unread: messages.filter((x) => !x.read).length,
        });
      } catch (e) {}
    }
    load();
  }, []);

  const cards = [
    { key: "pending", label: t(lang, "admin.pending") + " Buchungen", icon: ClipboardList, color: "text-terracotta", value: stats.pending, to: "/admin/bookings" },
    { key: "confirmed", label: t(lang, "admin.confirmed"), icon: TrendingUp, color: "text-sage", value: stats.confirmed, to: "/admin/bookings" },
    { key: "msgs", label: "Ungelesen", icon: Mail, color: "text-terracotta", value: stats.msgs_unread, to: "/admin/messages" },
    { key: "vouchers", label: "Offene Gutscheine", icon: Gift, color: "text-ink", value: stats.vouchers_pending, to: "/admin/vouchers" },
    { key: "customers", label: t(lang, "admin.customers"), icon: Users, color: "text-ink", value: stats.customers, to: "/admin/customers" },
    { key: "services", label: t(lang, "admin.services"), icon: Calendar, color: "text-ink", value: stats.services, to: "/admin/services" },
  ];

  return (
    <div className="p-5 sm:p-8 lg:p-10" data-testid="admin-dashboard">
      <h1 className="font-display text-3xl sm:text-4xl text-ink">{t(lang, "admin.dashboard")}</h1>
      <div className="mt-6 sm:mt-8 grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
        {cards.map((c) => (
          <Link key={c.key} to={c.to} className="bg-white border border-border rounded-md p-4 sm:p-6 hover:border-terracotta/40 transition-colors" data-testid={`stat-${c.key}`}>
            <c.icon className={`w-5 h-5 ${c.color} mb-2 sm:mb-3`} strokeWidth={1.5}/>
            <div className="text-[10px] sm:text-xs uppercase tracking-widest text-ink-muted">{c.label}</div>
            <div className="mt-1 sm:mt-2 font-display text-3xl sm:text-4xl text-ink">{c.value}</div>
          </Link>
        ))}
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-5 sm:gap-6">
        <div className="bg-white border border-border rounded-md p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl sm:text-2xl">Letzte Buchungen</h2>
            <Link to="/admin/bookings" className="text-sm text-terracotta hover:underline">→</Link>
          </div>
          {recent.length === 0 ? (
            <div className="text-sm text-ink-muted">—</div>
          ) : (
            <ul className="divide-y divide-border">
              {recent.map((b) => (
                <li key={b.id} className="py-3 flex items-center justify-between text-sm">
                  <div>
                    <div className="text-ink">{b.customer_name}</div>
                    <div className="text-xs text-ink-muted">{b.service_name_snapshot} · {b.date} {b.time}</div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${b.status === "confirmed" ? "bg-sage/15 text-sage" : b.status === "cancelled" ? "bg-destructive/10 text-destructive" : "bg-terracotta/10 text-terracotta"}`}>{t(lang, `admin.${b.status}`)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-border rounded-md p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl sm:text-2xl">Neue Nachrichten</h2>
            <Link to="/admin/messages" className="text-sm text-terracotta hover:underline">→</Link>
          </div>
          {recentMsgs.length === 0 ? (
            <div className="text-sm text-ink-muted">—</div>
          ) : (
            <ul className="divide-y divide-border">
              {recentMsgs.map((m) => (
                <li key={m.id} className="py-3 text-sm">
                  <div className="flex items-center justify-between">
                    <div className="text-ink font-medium">{m.name}</div>
                    <span className="text-xs text-ink-muted">{(m.created_at || "").slice(0, 10)}</span>
                  </div>
                  <div className="text-xs text-ink-muted truncate mt-1 max-w-md">{m.message}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
